import axiosInstance from '../axiosConfig';
import {
  ClassLevel,
  ClassLevelCreatePayload,
  ClassLevelUpdatePayload,
  EnrollSwimmerPayload,
  EnrollSwimmerResponse,
  EnrollmentAttendanceResponse,
  SwimEnrollment,
  RosterResponse,
  SwimmerAssessment,
  CreateAssessmentPayload,
  AttendanceRecordInput,
  MarkAttendanceResponse,
  MyCalendarEntry,
  ProgramSessionsResponse,
  RSVPStatus,
  CoachCalendarResponse,
  CompetitionUpsertPayload,
  SwimCompetitionRow,
  SwimEventRow,
  SwimEventUpsertPayload,
  CompetitionAttendanceResponse,
  EventHouseholdSwimmer,
  MeetEvent,
  SessionStaffEntry,
  ApiListEnvelope,
  ApiItemEnvelope,
  CompetitionRoster,
  EventRoster,
  TeamSwimmer,
  SwimLeague,
  LeagueUpsertPayload,
} from '../../types/swim';

/**
 * Swim Service — wrapper for Phase 2 swim endpoints.
 */
class SwimService {
  // ---------- Class Levels ----------

  async listClassLevels(
    opts: { includeInactive?: boolean; audience?: 'kids' | 'adult' } = {},
  ): Promise<ClassLevel[]> {
    const params: Record<string, string> = {};
    if (opts.includeInactive) params.include_inactive = 'true';
    if (opts.audience) params.audience = opts.audience;
    const res = await axiosInstance.get<ApiListEnvelope<ClassLevel>>('/class-levels/', { params });
    return res.data.data;
  }

  async createClassLevel(payload: ClassLevelCreatePayload): Promise<ClassLevel> {
    const res = await axiosInstance.post<ApiItemEnvelope<ClassLevel>>('/class-levels/', payload);
    return res.data.data;
  }

  async updateClassLevel(id: string, payload: ClassLevelUpdatePayload): Promise<ClassLevel> {
    const res = await axiosInstance.put<ApiItemEnvelope<ClassLevel>>(`/class-levels/${id}`, payload);
    return res.data.data;
  }

  async purgeClassLevel(id: string): Promise<void> {
    await axiosInstance.delete(`/class-levels/${id}/permanent`);
  }

  async deactivateClassLevel(id: string): Promise<ClassLevel> {
    const res = await axiosInstance.delete<ApiItemEnvelope<ClassLevel>>(`/class-levels/${id}`);
    return res.data.data;
  }

  async reorderClassLevels(orderedIds: string[]): Promise<ClassLevel[]> {
    const res = await axiosInstance.post<ApiListEnvelope<ClassLevel>>('/class-levels/reorder', {
      ordered_ids: orderedIds,
    });
    return res.data.data;
  }

  // ---------- Enrollment ----------

  async enrollSwimmer(payload: EnrollSwimmerPayload): Promise<EnrollSwimmerResponse> {
    const res = await axiosInstance.post<EnrollSwimmerResponse>('/swim-enrollments/', payload);
    return res.data;
  }

  async myEnrollments(): Promise<SwimEnrollment[]> {
    const res = await axiosInstance.get<ApiListEnvelope<SwimEnrollment>>('/swim-enrollments/my');
    return res.data.data;
  }

  async cancelEnrollment(enrollmentId: string, reason?: string): Promise<SwimEnrollment> {
    const res = await axiosInstance.post<ApiItemEnvelope<SwimEnrollment>>(
      `/swim-enrollments/${enrollmentId}/cancel`,
      reason ? { reason } : {},
    );
    return res.data.data;
  }

  async listCancellations(limit = 100): Promise<SwimEnrollment[]> {
    const res = await axiosInstance.get<ApiListEnvelope<SwimEnrollment>>(
      '/swim-enrollments/cancellations',
      { params: { limit: String(limit) } },
    );
    return res.data.data;
  }

  async transferEnrollment(enrollmentId: string, targetProgramId: string): Promise<SwimEnrollment> {
    const res = await axiosInstance.post<ApiItemEnvelope<SwimEnrollment>>(
      `/swim-enrollments/${enrollmentId}/transfer`,
      { target_program_id: targetProgramId },
    );
    return res.data.data;
  }

  async listPending(olderThanHours?: number): Promise<SwimEnrollment[]> {
    const params: Record<string, string> = {};
    if (olderThanHours !== undefined) params.older_than_hours = String(olderThanHours);
    const res = await axiosInstance.get<ApiListEnvelope<SwimEnrollment>>('/swim-enrollments/pending', { params });
    return res.data.data;
  }

  async confirmEnrollment(enrollmentId: string): Promise<SwimEnrollment> {
    const res = await axiosInstance.post<ApiItemEnvelope<SwimEnrollment>>(
      `/swim-enrollments/${enrollmentId}/confirm`,
    );
    return res.data.data;
  }

  async bulkConfirmEnrollments(enrollmentIds: string[]): Promise<{ confirmed: number; skipped: number }> {
    const res = await axiosInstance.post<{ success: boolean; confirmed: number; skipped: number }>(
      '/swim-enrollments/bulk-confirm',
      { enrollment_ids: enrollmentIds },
    );
    return { confirmed: res.data.confirmed, skipped: res.data.skipped };
  }

  // ---------- Roster ----------

  async getRoster(programId: string, opts: { date?: string } = {}): Promise<RosterResponse> {
    const params: Record<string, string> = {};
    if (opts.date) params.date = opts.date;
    const res = await axiosInstance.get<RosterResponse>(
      `/swim-enrollments/program/${programId}/roster`,
      { params },
    );
    return res.data;
  }

  // ---------- Assessments ----------

  async createAssessment(payload: CreateAssessmentPayload): Promise<SwimmerAssessment> {
    const res = await axiosInstance.post<ApiItemEnvelope<SwimmerAssessment>>(
      '/swimmer-assessments/',
      payload,
    );
    return res.data.data;
  }

  async listAssessmentsForDependent(dependentId: string): Promise<SwimmerAssessment[]> {
    const res = await axiosInstance.get<ApiListEnvelope<SwimmerAssessment>>(
      `/swimmer-assessments/swimmer/dependent/${dependentId}`,
    );
    return res.data.data;
  }

  async listAssessmentsForNonMemberDependent(nonMemberDependentId: number): Promise<SwimmerAssessment[]> {
    const res = await axiosInstance.get<ApiListEnvelope<SwimmerAssessment>>(
      `/swimmer-assessments/swimmer/non-member-dependent/${nonMemberDependentId}`,
    );
    return res.data.data;
  }

  // ---------- Attendance ----------

  async markAttendance(
    programSessionId: string,
    records: AttendanceRecordInput[],
  ): Promise<MarkAttendanceResponse> {
    const res = await axiosInstance.post<MarkAttendanceResponse>(
      `/swim-attendance/session/${programSessionId}`,
      { records },
    );
    return res.data;
  }

  async getSessionAttendance(programSessionId: string) {
    const res = await axiosInstance.get(`/swim-attendance/session/${programSessionId}`);
    return res.data;
  }

  async getEnrollmentAttendance(enrollmentId: string): Promise<EnrollmentAttendanceResponse> {
    const res = await axiosInstance.get<EnrollmentAttendanceResponse>(
      `/swim-attendance/enrollment/${enrollmentId}`,
    );
    return res.data;
  }

  // ---------- Calendar / RSVP ----------

  async myCalendar(): Promise<{ entries: MyCalendarEntry[]; competitions: SwimCompetitionRow[]; events: SwimEventRow[] }> {
    const res = await axiosInstance.get<
      ApiListEnvelope<MyCalendarEntry> & {
        competitions?: SwimCompetitionRow[];
        events?: SwimEventRow[];
      }
    >('/swim-sessions/my-calendar');
    return {
      entries: res.data.data ?? [],
      competitions: res.data.competitions ?? [],
      events: res.data.events ?? [],
    };
  }

  async setRSVP(
    enrollmentId: string,
    programSessionId: string,
    status: RSVPStatus,
  ): Promise<void> {
    await axiosInstance.put('/swim-sessions/rsvp', {
      enrollment_id: enrollmentId,
      program_session_id: programSessionId,
      status,
    });
  }

  async getWeeklyAvailability(
    enrollmentId: string,
  ): Promise<{ available_days: number[]; program_days: number[] }> {
    const res = await axiosInstance.get<{ data: { available_days: number[]; program_days: number[] } }>(
      `/swim-sessions/enrollments/${enrollmentId}/weekly-availability`,
    );
    return res.data.data;
  }

  async setWeeklyAvailability(
    enrollmentId: string,
    availableDays: number[],
  ): Promise<{ planned: number; skipped: number; total: number }> {
    const res = await axiosInstance.put<{ data: { planned: number; skipped: number; total: number } }>(
      `/swim-sessions/enrollments/${enrollmentId}/weekly-availability`,
      { available_days: availableDays },
    );
    return res.data.data;
  }

  // ---------- Competition attendance + meet events ----------

  async listMeetEvents(): Promise<MeetEvent[]> {
    const res = await axiosInstance.get<{ data: MeetEvent[] }>('/swim-sessions/meet-events');
    return res.data.data ?? [];
  }

  async getCompetitionAttendance(competitionId: string): Promise<CompetitionAttendanceResponse> {
    const res = await axiosInstance.get<{ data: CompetitionAttendanceResponse }>(
      `/swim-sessions/competitions/${competitionId}/attendance`,
    );
    return res.data.data;
  }

  async setCompetitionAttendance(
    competitionId: string,
    swimmer: { member_id?: string | null; non_member_dependent_id?: number | null },
    attending: boolean,
    eventCodes: string[],
  ): Promise<{ attending: boolean; event_codes: string[] }> {
    const res = await axiosInstance.put<{ data: { attending: boolean; event_codes: string[] } }>(
      `/swim-sessions/competitions/${competitionId}/attendance`,
      {
        member_id: swimmer.member_id ?? undefined,
        non_member_dependent_id: swimmer.non_member_dependent_id ?? undefined,
        attending,
        event_codes: eventCodes,
      },
    );
    return res.data.data;
  }

  async programSessions(programId: string): Promise<ProgramSessionsResponse> {
    const res = await axiosInstance.get<ProgramSessionsResponse>(
      `/swim-sessions/program/${programId}`,
    );
    return res.data;
  }

  async setSessionWorkout(sessionId: string, workout: string | null): Promise<void> {
    await axiosInstance.put(`/swim-sessions/${sessionId}/workout`, { workout });
  }

  // ---------- Coach month calendar ----------

  async coachCalendar(startIso: string, endIso: string): Promise<CoachCalendarResponse> {
    const res = await axiosInstance.get<CoachCalendarResponse>('/swim-sessions/calendar', {
      params: { start: startIso, end: endIso },
    });
    return res.data;
  }

  async setDailyWorkout(planDate: string, workout: string | null): Promise<void> {
    await axiosInstance.put(`/swim-sessions/calendar/day/${planDate}`, { workout });
  }

  async upsertCompetition(payload: CompetitionUpsertPayload): Promise<SwimCompetitionRow> {
    const res = await axiosInstance.post<ApiItemEnvelope<SwimCompetitionRow>>(
      '/swim-sessions/calendar/competitions',
      payload,
    );
    return res.data.data;
  }

  async deleteCompetition(competitionId: string): Promise<void> {
    await axiosInstance.delete(`/swim-sessions/calendar/competitions/${competitionId}`);
  }

  // ---------- Generic events ----------

  async upsertEvent(payload: SwimEventUpsertPayload): Promise<SwimEventRow> {
    const res = await axiosInstance.post<ApiItemEnvelope<SwimEventRow>>(
      '/swim-sessions/calendar/events',
      payload,
    );
    return res.data.data;
  }

  async deleteEvent(eventId: string): Promise<void> {
    await axiosInstance.delete(`/swim-sessions/calendar/events/${eventId}`);
  }

  // ---------- Session staff (multi-coach assignment) ----------

  async getSessionStaff(sessionId: string): Promise<SessionStaffEntry[]> {
    const res = await axiosInstance.get<ApiListEnvelope<SessionStaffEntry>>(
      `/swim-sessions/${sessionId}/staff`,
    );
    return res.data.data ?? [];
  }

  async setSessionStaff(
    sessionId: string,
    assignments: { staff_id: string; role?: 'instructor' | 'assistant' | 'sub' }[],
  ): Promise<SessionStaffEntry[]> {
    const res = await axiosInstance.put<ApiListEnvelope<SessionStaffEntry>>(
      `/swim-sessions/${sessionId}/staff`,
      { assignments },
    );
    return res.data.data ?? [];
  }

  // ---------- Team-wide attendance (staff/coach) ----------

  /** Every swimmer attending this meet + their entered events (team-wide). */
  async getCompetitionRoster(competitionId: string): Promise<CompetitionRoster> {
    const res = await axiosInstance.get<ApiItemEnvelope<CompetitionRoster>>(
      `/swim-sessions/competitions/${competitionId}/roster`,
    );
    return res.data.data;
  }

  /** Every member RSVP'd to this club event (team-wide). */
  async getEventRoster(eventId: string): Promise<EventRoster> {
    const res = await axiosInstance.get<ApiItemEnvelope<EventRoster>>(
      `/swim-sessions/events/${eventId}/roster`,
    );
    return res.data.data;
  }

  /** Parent: household swimmers + whether each is RSVP'd to this club event. */
  async getEventHouseholdAttendance(eventId: string): Promise<EventHouseholdSwimmer[]> {
    const res = await axiosInstance.get<{ data: { swimmers: EventHouseholdSwimmer[] } }>(
      `/swim-sessions/events/${eventId}/household-attendance`,
    );
    return res.data.data.swimmers ?? [];
  }

  /** RSVP a member to a club event (presence of a row = attending). */
  async setEventAttendance(
    eventId: string,
    swimmer: { member_id?: string | null; non_member_dependent_id?: number | null },
    attending: boolean,
  ): Promise<void> {
    await axiosInstance.put(`/swim-sessions/events/${eventId}/attendance`, {
      member_id: swimmer.member_id ?? undefined,
      non_member_dependent_id: swimmer.non_member_dependent_id ?? undefined,
      attending,
    });
  }

  /** Every active swimmer on the team — picker for adding someone to a meet/event. */
  async getTeamSwimmers(competitionId?: string): Promise<TeamSwimmer[]> {
    const res = await axiosInstance.get<ApiItemEnvelope<{ swimmers: TeamSwimmer[] }>>(
      '/swim-sessions/swimmers',
      { params: competitionId ? { competition_id: competitionId } : {} },
    );
    return res.data.data.swimmers ?? [];
  }

  // ---------- Swim leagues (admin) ----------

  async getLeagues(): Promise<SwimLeague[]> {
    const res = await axiosInstance.get<{ data: SwimLeague[] }>('/swim-sessions/leagues');
    return res.data.data ?? [];
  }

  async upsertLeague(payload: LeagueUpsertPayload): Promise<SwimLeague> {
    const res = await axiosInstance.post<ApiItemEnvelope<SwimLeague>>('/swim-sessions/leagues', payload);
    return res.data.data;
  }

  async deleteLeague(id: string): Promise<void> {
    await axiosInstance.delete(`/swim-sessions/leagues/${id}`);
  }
}

const swimService = new SwimService();
export default swimService;
